// The HOW IT PLAYS card (player-reach.md §8.7, R2445).
//
// One card with one page for each mode: CONQUEST, ARCADE, EVENT and CLAN
// WAR, behind a switch of four presses at the top (R2445, R3178). Each
// page states four rows: THE GOAL, THE LOOP, TOGETHER and WHAT STAYS.
// The foot says what the drills teach, for every mode, and one press opens
// the drill board of the arcade (`ARCADE_DRILLS_HREF`). The conquest page
// adds that the sea trials teach the economy, because a drill holds no
// conquest economy.
//
// Four places open it: the `?` press of each landing console (a screen
// with no session), the `?` press of each mode tile of the hub PLAY
// panel, the HOW IT PLAYS row of SUPPORT · QUESTIONS, and the press of
// the SEASON block of the welcome card. The card therefore stands on
// `document.body` under its own root `#howplays`, and no host screen
// rewrites it: the landing writes its whole root again when a readout
// lands, and the card must stay open through that.
//
// Every figure comes from the compile-time lane (`gen/balance`): the
// season days, the squad size and the clan size. The card states no drill
// minutes, because only the arcade manifest carries them and the landing
// reads no manifest.
//
// The card is a modal dialog. It takes the focus on its close press, keeps
// the Tab key inside, and gives the focus back to the opener when it
// closes. The ✕ press, a press on the shade and Escape close it. Its body
// scrolls on a telephone, so every press acts on the release (§16.8,
// R1206) through its own `PressRoute`.

import { MAX_CLAN_MEMBERS, SEASON_DAYS, SQUAD_SIZE } from '../gen/balance';
import { howPlays as S, hubPlay as MODE } from '../gen/strings';
import { esc } from './esc';
import { adoptLightRoot, releaseLightRoot } from './lightchrome';
import { ARCADE_DRILLS_HREF } from './hublink';
import { assignWithGesture } from './nav';
import { PressRoute } from './pressroute';

/** The four pages of the card, in the order of the switch. */
export const HOW_MODES = ['conquest', 'arcade', 'event', 'clanwar'] as const;
export type HowMode = typeof HOW_MODES[number];

/** The id of the card root. */
export const HOW_ROOT_ID = 'howplays';

/** One row of a page: the head and the sentence. */
export interface HowRow {
  key: 'goal' | 'loop' | 'together' | 'stays';
  head: string;
  text: string;
}

/** The four rows of one page, in the reading order. */
export function howRows(mode: HowMode): HowRow[] {
  if (mode === 'conquest') {
    return [
      { key: 'goal', head: S.head_goal, text: S.core_goal({ days: SEASON_DAYS }) },
      { key: 'loop', head: S.head_loop, text: S.core_loop },
      {
        key: 'together', head: S.head_together,
        text: S.core_together({ squad: SQUAD_SIZE, members: MAX_CLAN_MEMBERS }),
      },
      { key: 'stays', head: S.head_stays, text: S.core_stays },
    ];
  }
  const [goal, loop, together, stays] = mode === 'arcade'
    ? [S.arcade_goal, S.arcade_loop, S.arcade_together, S.arcade_stays]
    : mode === 'event'
      ? [S.event_goal, S.event_loop, S.event_together, S.event_stays]
      : [S.clanwar_goal, S.clanwar_loop, S.clanwar_together, S.clanwar_stays];
  return [
    { key: 'goal', head: S.head_goal, text: goal },
    { key: 'loop', head: S.head_loop, text: loop },
    { key: 'together', head: S.head_together, text: together },
    { key: 'stays', head: S.head_stays, text: stays },
  ];
}

/** The mode name of a page: the word of the hub PLAY tile (R2208). */
export function modeName(mode: HowMode): string {
  return mode === 'conquest' ? MODE.mode_core_name
    : mode === 'arcade' ? MODE.mode_arcade_name
      : mode === 'event' ? MODE.mode_event_name : MODE.mode_clanwar_name;
}

/**
 * The `?` press that opens the card on one page. `act` is the host's own
 * press word, and the press carries the page as `data-mode`. The host
 * styles the press; the accessible name names the mode.
 */
export function howPressHtml(act: string, mode: HowMode, cls = 'how'): string {
  return `<button type="button" class="${cls}" data-act="${esc(act)}" data-mode="${mode}"`
    + ` aria-label="${esc(S.open_label({ mode: modeName(mode) }))}"><span aria-hidden="true">?</span></button>`;
}

/** The page a press names, or conquest for a word the card does not know. */
export function howModeOf(word: string | undefined): HowMode {
  return HOW_MODES.find((m) => m === word) ?? 'conquest';
}

/** The card's markup for one page. */
export function howPlaysHtml(mode: HowMode): string {
  const tab = (m: HowMode) => `<button type="button" class="hp-tab${m === mode ? ' on' : ''}" role="radio"`
    + ` aria-checked="${m === mode}" data-hp="mode" data-mode="${m}">${esc(modeName(m))}</button>`;
  const rows = howRows(mode).map((r) => `<div class="hp-row" data-row="${r.key}">`
    + `<h3>${esc(r.head)}</h3><p>${esc(r.text)}</p></div>`).join('');
  return '<div class="hp-shade" data-hp="close"></div>'
    + `<div class="hp-card ${mode}" role="dialog" aria-modal="true" aria-labelledby="hp-title">`
    + '<div class="hp-head">'
    + `<h2 id="hp-title">${esc(S.title)}</h2>`
    + `<button type="button" class="hp-x" data-hp="close" aria-label="${esc(S.close)}">✕</button>`
    + '</div>'
    + `<div class="hp-switch" role="radiogroup" aria-label="${esc(S.switch_label)}">`
    + `${HOW_MODES.map(tab).join('')}</div>`
    + `<div class="hp-body">${rows}</div>`
    + '<div class="hp-foot">'
    + `<p>${esc(S.drill_line)}</p>`
    + (mode === 'conquest' ? `<p class="dim">${esc(S.trials_line)}</p>` : '')
    + `<button type="button" class="hp-go" data-hp="drill" title="${esc(S.drill_title)}">${esc(S.drill_go)}</button>`
    + '</div></div>';
}

/** What a host hands the card. */
export interface HowPlaysOptions {
  /** Plays the press cue of the host, where it has one. */
  press?: () => void;
  /** Leaves for the drill board. A test hands a stub; the default leaves
   *  the page with the press's activation (`ui/nav.ts`). */
  drill?: () => void;
}

/** The card open now, so a second open turns the page and makes no second
 *  card. */
let current: HowPlaysCard | null = null;

/** Opens the card on one page, or turns the open card to it. */
export function openHowPlays(mode: HowMode, opts: HowPlaysOptions = {}): HowPlaysCard {
  if (current !== null && current.isOpen()) {
    current.show(mode);
    return current;
  }
  current = new HowPlaysCard(mode, opts);
  return current;
}

/** Closes the open card, if one stands. A host that leaves calls it. */
export function closeHowPlays(): void {
  current?.close();
}

/** One open card on `document.body`. */
export class HowPlaysCard {
  private readonly root = document.createElement('div');
  private readonly route: PressRoute;
  private readonly opener: Element | null;
  private mode: HowMode;
  private open = true;

  constructor(mode: HowMode, private readonly opts: HowPlaysOptions) {
    this.mode = mode;
    this.opener = document.activeElement;
    this.root.id = HOW_ROOT_ID;
    // A cover outside `#ui` (ui-map-interface.md §2.5, R3320).
    adoptLightRoot(this.root);
    this.root.addEventListener('keydown', this.onKey);
    document.body.appendChild(this.root);
    this.route = new PressRoute(this.root, {
      control: '[data-hp]',
      scrolls: () => true,
      act: (el) => this.act(el),
    });
    this.render();
    this.root.querySelector<HTMLElement>('.hp-x')?.focus();
  }

  /** True until the card closes. */
  isOpen(): boolean {
    return this.open;
  }

  /** The page the card shows. */
  page(): HowMode {
    return this.mode;
  }

  /** Turns the card to one page. */
  show(mode: HowMode): void {
    if (!this.open || mode === this.mode) return;
    this.mode = mode;
    this.render();
    this.root.querySelector<HTMLElement>(`.hp-tab[data-mode="${mode}"]`)?.focus();
  }

  /** Removes the card and gives the focus back to the opener. */
  close(): void {
    if (!this.open) return;
    this.open = false;
    this.route.destroy();
    this.root.removeEventListener('keydown', this.onKey);
    this.root.remove();
    releaseLightRoot(this.root);
    if (current === this) current = null;
    if (this.opener instanceof HTMLElement && this.opener.isConnected) this.opener.focus();
  }

  private render(): void {
    this.root.innerHTML = howPlaysHtml(this.mode);
  }

  private act(el: HTMLElement): void {
    this.opts.press?.();
    switch (el.dataset.hp) {
      case 'close': this.close(); return;
      case 'mode': this.show(howModeOf(el.dataset.mode)); return;
      case 'drill':
        (this.opts.drill ?? (() => assignWithGesture(ARCADE_DRILLS_HREF)))();
    }
  }

  /** Escape closes the card, and Tab walks inside it. The key reaches no
   *  host screen under the card. */
  private onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      this.close();
      return;
    }
    if (ev.key !== 'Tab') {
      ev.stopPropagation();
      return;
    }
    const list = [...this.root.querySelectorAll<HTMLElement>('button')];
    if (list.length === 0) return;
    const first = list[0]!;
    const last = list[list.length - 1]!;
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  };
}
